import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { pickLocale } from "@/lib/localize";
import { searchLibrary } from "@/lib/libraryApi";
import { LibraryBreadcrumb } from "@/components/LibraryBreadcrumb";

/**
 * Search the Living Library. Phase 1: interface + architecture — a plain
 * cross-language search over published entries. As the Library grows
 * (and later spans lessons, guides, articles, videos) only the backend
 * search deepens; this surface stays.
 */
export default function LibrarySearch() {
  const { t, lang } = useT();
  const [q, setQ] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const [results, setResults] = useState([]);

  useSEO({
    title: `${t("library.search.title")} · ${t("library.hero.title")} · StoicOs.ai`,
    description: t("library.search.sub"),
    canonical: "https://stoicos.ai/library/search",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setState("loading");
    try {
      const data = await searchLibrary(term);
      setResults(data.entries || []);
    } catch {
      setResults([]);
    }
    setState("done");
  };

  return (
    <main data-testid="library-search-page" className="bg-ink text-bone min-h-screen page-fade-in">
      <section className="relative pt-40 md:pt-52 pb-28 md:pb-40 px-6 sm:px-10">
        <div className="max-w-2xl mx-auto">

          <div className="mb-12">
            <LibraryBreadcrumb
              items={[
                { label: t("academy.nav"), to: "/academy" },
                { label: t("library.hero.title"), to: "/library" },
                { label: t("library.search.title") },
              ]}
              testid="library-search-breadcrumb"
            />
          </div>

          <h1 data-testid="library-search-title" className="font-serif text-4xl sm:text-5xl text-bone leading-[1.08] tracking-tight">
            {t("library.search.title")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-fog font-reading leading-[1.9]">
            {t("library.search.sub")}
          </p>

          <form onSubmit={onSubmit} className="mt-12 flex gap-3" data-testid="library-search-form">
            <div className="relative flex-1">
              <Search size={16} strokeWidth={1.5}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("library.search.placeholder")}
                data-testid="library-search-input"
                className="w-full bg-white/[0.03] border border-white/15 focus:border-gold/60
                  text-bone placeholder:text-bone/35 pl-11 pr-4 py-3.5 text-sm font-reading
                  outline-none transition-colors"
              />
            </div>
            <button
              type="submit"
              data-testid="library-search-submit"
              className="px-6 md:px-8 bg-gold hover:bg-gold-hover text-ink text-[11px] tracking-[0.28em]
                uppercase font-semibold transition-colors"
            >
              {t("library.search.button")}
            </button>
          </form>

          <div className="mt-12">
            {state === "idle" && (
              <p data-testid="library-search-hint" className="text-sm text-fog italic font-serif">
                {t("library.search.hint")}
              </p>
            )}
            {state === "loading" && (
              <p className="text-sm text-fog italic font-serif">…</p>
            )}
            {state === "done" && results.length === 0 && (
              <div
                data-testid="library-search-empty"
                className="border border-gold/20 bg-gold/[0.02] px-8 py-12 text-center"
              >
                <p className="font-serif text-lg md:text-xl text-bone/90 leading-relaxed max-w-md mx-auto">
                  {t("library.search.empty")}
                </p>
                <p className="mt-3 font-serif italic text-base md:text-lg text-gold/85">
                  {t("library.search.empty2")}
                </p>
                <Link
                  to="/library"
                  data-testid="library-search-explore"
                  className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase
                    text-bone/70 hover:text-gold transition-colors"
                >
                  {t("library.search.explore")}
                </Link>
              </div>
            )}
            {state === "done" && results.length > 0 && (
              <ul data-testid="library-search-results" className="flex flex-col gap-4">
                {results.map((e) => (
                  <li key={e.id || e.slug}>
                    <Link
                      to={`/library/${e.category}/${e.slug}`}
                      className="block border border-white/10 hover:border-gold/40 p-6
                        transition-[transform,box-shadow,border-color] duration-300
                        hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-22px_rgba(232,200,121,0.3)]"
                    >
                      <p className="text-[9px] tracking-[0.28em] uppercase text-gold/70">
                        {t(`library.cats.${e.category}.title`)}
                      </p>
                      <h3 className="mt-2 font-serif text-xl text-bone">{pickLocale(e.title, lang)}</h3>
                      {e.summary && (
                        <p className="mt-2 text-sm text-fog font-reading leading-relaxed">
                          {pickLocale(e.summary, lang)}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-14">
            <Link
              to="/library"
              data-testid="library-search-back"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors"
            >
              <ArrowLeft size={13} strokeWidth={1.75} />
              {t("library.category.back")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
